import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import trophiaLogo from "../../assets/trophiaLogo.png";
import classes from "./Sidebar.module.scss";

const NAV_ITEMS = [
  {
    id: "Dashboard",
    path: "/dashboard",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: "Appointments",
    path: "/appointments",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    id: "Diet Plans",
    path: "/diet-plans",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
      </svg>
    ),
  },
  {
    id: "Progress",
    path: "/progress",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
];

type SidebarProps = {
  onLogout?: () => void;
};

const Sidebar = ({ onLogout }: SidebarProps) => {
  const navigate        = useNavigate();
  const location        = useLocation();
  const { logout, user} = useAuth();
  const [open, setOpen] = useState(false);

  const initials = (name: string) =>
    name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  const handleNav = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    if (onLogout) onLogout();
    navigate("/", { replace: true });
    setOpen(false);
  };

  const inner = (
    <>
      <div className={classes.logo}>
        <img src={trophiaLogo} alt="Trophia" className={classes.logo_img} />
      </div>

      <nav className={classes.nav}>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.id}
              type="button"
              className={`${classes.nav_item} ${isActive ? classes.nav_active : ""}`}
              onClick={() => handleNav(item.path)}
            >
              {item.icon}
              {item.id}
            </button>
          );
        })}
      </nav>

      <div className={classes.sidebar_footer}>
        <div className={classes.footer_avatar}>
          {user?.name ? initials(user.name) : "?"}
        </div>
        <div className={classes.footer_info}>
          <div className={classes.footer_name}>{user?.name ?? "Client"}</div>
          <div className={classes.footer_role}>Client</div>
        </div>
        <div className={classes.logout_wrap}>
          <button type="button" className={classes.logout_btn} onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
          <span className={classes.logout_label}>Log out</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* ── Desktop sidebar ── */}
      <aside className={classes.sidebar}>
        {inner}
      </aside>

      {/* ── Mobile: hamburger button ── */}
      <button
        type="button"
        className={classes.burger}
        onClick={() => setOpen(true)}
        aria-label="Open menu"
      >
        <span className={classes.burger_bar} />
        <span className={classes.burger_bar} />
        <span className={classes.burger_bar} />
      </button>

      {/* ── Mobile: backdrop ── */}
      {open && (
        <div className={classes.backdrop} onClick={() => setOpen(false)} />
      )}

      {/* ── Mobile: drawer ── */}
      <aside className={`${classes.drawer} ${open ? classes.drawer_open : ""}`}>
        {/* Close button inside drawer */}
        <button
          type="button"
          className={classes.drawer_close}
          onClick={() => setOpen(false)}
          aria-label="Close menu"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {inner}
      </aside>
    </>
  );
};

export default Sidebar;
